<template>
  <v-container class="nebentaetigkeiten-container">
    <!-- Formular für neue Nebentätigkeit mit Glasmorphismus-Effekt -->
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2"
          >mdi-clock-outline</v-icon
        >
        Neue Nebentätigkeit
        <v-spacer></v-spacer>

        <!-- Toggle für Eingabemodus -->
        <v-btn-toggle
          v-model="eingabeModus"
          color="success"
          variant="outlined"
          density="comfortable"
          rounded
        >
          <v-btn value="einfach" prepend-icon="mdi-clock">Einfach</v-btn>
          <v-btn value="detailliert" prepend-icon="mdi-calendar-clock"
            >Detailliert</v-btn
          >
        </v-btn-toggle>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <v-card-text class="py-4">
        <!-- Einfache Eingabemaske -->
        <div v-if="eingabeModus === 'einfach'">
          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="einfachDatum"
                label="Datum"
                type="date"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-calendar</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="8" class="d-flex align-center">
              <div style="width:100%">
                <v-text-field
                  v-model="einfachDurationInput"
                  label="Dauer (Minuten oder HH:MM)"
                  placeholder="z.B. 90 oder 1:30"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  @blur="parseEinfachDurationInput"
                  color="success"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-timer-outline</v-icon>
                  </template>
                  <template v-slot:append-inner>
                    <span class="text-caption">min</span>
                  </template>
                </v-text-field>

                <div class="text-caption" style="margin-top:6px;color:var(--v-theme-on-surface);">
                  Berechnet: <strong>{{ computedEinfachTotalMinutes }} Minuten</strong>
                </div>
              </div>
            </v-col>

            <v-col cols="12">
              <div class="d-flex align-center gap-2 mb-4">
                <v-select
                  v-model="kommentar"
                  :items="alleKommentare"
                  label="Vordefinierter Kommentar"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  clearable
                  class="form-field flex-grow-1"
                  color="success"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-format-list-bulleted</v-icon>
                  </template>
                </v-select>
                <v-btn
                  icon="mdi-cog"
                  color="success"
                  variant="outlined"
                  @click="vorlagenDialog = true"
                  title="Vorlagen verwalten"
                ></v-btn>
              </div>

              <v-textarea
                v-model="kommentar"
                label="Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                placeholder="Eigener Kommentar..."
                auto-grow
                rows="2"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-comment-text-outline</v-icon>
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </div>

        <!-- Detaillierte Eingabemaske -->
        <div v-else>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="startzeit"
                label="Startzeit"
                type="datetime-local"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-start</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="endzeit"
                label="Endzeit"
                type="datetime-local"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-end</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="kommentar"
                :items="alleKommentare"
                label="Vordefinierter Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                clearable
                class="form-field mb-4"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-format-list-bulleted</v-icon
                  >
                </template>
              </v-select>

              <v-textarea
                v-model="kommentar"
                label="Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                placeholder="Eigener Kommentar..."
                auto-grow
                rows="2"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-comment-text-outline</v-icon
                  >
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </div>

        <!-- Verbesserte Action-Schaltfläche -->
        <v-btn
          block
          color="success"
          class="mt-6 submit-btn"
          size="large"
          elevation="2"
          @click="speichereNebentaetigkeit"
          :disabled="!isFormValid"
        >
          <v-icon start>mdi-content-save</v-icon>
          Nebentätigkeit speichern
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Darstellung gespeicherter Nebentätigkeiten -->
    <v-card class="status-overview-card">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2"
          >mdi-format-list-checks</v-icon
        >
        Gespeicherte Nebentätigkeiten
        <v-spacer></v-spacer>

        <div class="d-flex align-center">
          <v-tooltip location="bottom" text="Suchen">
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                class="mx-1"
                variant="text"
                v-bind="props"
                @click="nebentaetigkeitenSearchDialog = true"
                color="success"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <!-- Verbesserte Tabelle mit modernem Design -->
      <v-data-table
        :headers="computedHeaders"
        :items="nebentaetigkeiten"
        density="comfortable"
        class="data-table"
        :items-per-page="10"
        :items-per-page-options="[5, 10, 20, 50]"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <div class="kommentar-cell" @click="toggleStartzeit(item)">
                {{ formatDate(item.startzeit) }}
                <v-tooltip activator="parent" location="top">
                  {{ formatDate(item.startzeit) }}
                </v-tooltip>
              </div>
            </td>

            <td>
              <div class="kommentar-cell">
                {{ formatTime(item.startzeit) }} Uhr
              </div>
            </td>

            <td>
              <div class="kommentar-cell">
                {{ formatTime(item.endzeit) }} Uhr
              </div>
            </td>

            <td>
              <div class="duration-chip" @click="toggleDuration(item)">
                <span v-if="item.showMinutes">
                  {{
                    calculateDurationInMinutes(item.startzeit, item.endzeit)
                  }}
                  Min
                </span>
                <span v-else>
                  {{ calculateDuration(item.startzeit, item.endzeit) }}
                </span>
                <v-tooltip activator="parent" location="top">
                  Klicken zum Umschalten zwischen Stunden und Minuten
                </v-tooltip>
              </div>
            </td>

            <td>
              <div class="kommentar-cell">
                {{ truncateText(item.kommentar, 40) }}
                <v-tooltip
                  v-if="item.kommentar && item.kommentar.length > 40"
                  activator="parent"
                  location="top"
                  max-width="300"
                >
                  {{ item.kommentar }}
                </v-tooltip>
              </div>
            </td>

            <td class="action-buttons-cell">
              <div class="action-menu-wrapper">
                <v-menu
                  location="bottom"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset="5"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      size="small"
                      v-bind="props"
                      variant="text"
                      class="action-menu-btn"
                      color="grey-lighten-1"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list density="compact" class="action-menu-list">
                    <v-list-item
                      @click="bearbeiteEintrag(item)"
                      prepend-icon="mdi-pencil"
                      title="Bearbeiten"
                      class="action-menu-item edit-action"
                      value="edit"
                    ></v-list-item>

                    <v-divider class="menu-divider"></v-divider>

                    <v-list-item
                      @click="confirmDelete(item.id)"
                      prepend-icon="mdi-delete"
                      title="Löschen"
                      class="action-menu-item delete-action"
                      value="delete"
                    ></v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </td>
          </tr>
        </template>

        <!-- Leerstand-Anzeige -->
        <template v-slot:no-data>
          <div class="no-data-message">
            <v-icon size="large" color="grey" class="mb-3"
              >mdi-database-off</v-icon
            >
            <p>Keine Nebentätigkeiten vorhanden</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Bearbeiten-Dialog -->
    <v-dialog v-model="editDialog" max-width="550" class="edit-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-pencil-box</v-icon>
          Nebentätigkeit bearbeiten
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editItem.startzeit"
                label="Startzeit"
                type="datetime-local"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-start</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editItem.endzeit"
                label="Endzeit"
                type="datetime-local"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-clock-end</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-select
                v-model="editItem.kommentar"
                :items="alleKommentare"
                label="Vordefinierter Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                clearable
                class="form-field mb-4"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-format-list-bulleted</v-icon>
                </template>
              </v-select>

              <v-textarea
                v-model="editItem.kommentar"
                label="Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                auto-grow
                rows="2"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-comment-text-outline</v-icon
                  >
                </template>
              </v-textarea>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="editDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            variant="elevated"
            @click="updateNebentaetigkeit"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Löschen-Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" class="delete-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title warning-header d-flex align-center">
          <v-icon color="error" size="24" class="mr-2">mdi-alert-circle</v-icon>
          Löschen bestätigen
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content text-center pa-6">
          <p>Bist du sicher, dass du diese Nebentätigkeit löschen möchtest?</p>
          <p class="text-caption mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="deleteDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="elevated"
            @click="deleteNebentaetigkeit"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Verbesserter Suchdialog -->
    <v-dialog
      v-model="nebentaetigkeitenSearchDialog"
      max-width="800"
      class="search-dialog"
    >
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-magnify</v-icon>
          Nebentätigkeiten durchsuchen
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="nebentaetigkeitenSearchDialog = false"
          ></v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <v-text-field
            v-model="nebentaetigkeitenSearchQuery"
            label="Suchbegriff eingeben..."
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            clearable
            hide-details
            class="search-field mb-4"
            prepend-inner-icon="mdi-magnify"
            @keyup.esc="nebentaetigkeitenSearchDialog = false"
            color="success"
          ></v-text-field>

          <!-- Suchergebnisse -->
          <div v-if="nebentaetigkeitenSearchQuery" class="search-results">
            <v-data-table
              :headers="headers"
              :items="filteredNebentaetigkeiten"
              density="comfortable"
              class="data-table search-table"
              hide-default-footer
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>
                    <div class="kommentar-cell">
                      {{ formatDate(item.startzeit) }}
                    </div>
                  </td>

                  <td>
                    <div class="kommentar-cell">
                      {{ formatTime(item.startzeit) }} Uhr
                    </div>
                  </td>

                  <td>
                    <div class="kommentar-cell">
                      {{ formatTime(item.endzeit) }} Uhr
                    </div>
                  </td>

                  <td>
                    <div class="kommentar-cell">
                      {{ calculateDuration(item.startzeit, item.endzeit) }}
                    </div>
                  </td>

                  <td>
                    <div class="kommentar-cell">
                      {{ truncateText(item.kommentar, 40) }}
                      <v-tooltip
                        v-if="item.kommentar && item.kommentar.length > 40"
                        activator="parent"
                        location="top"
                        max-width="300"
                      >
                        {{ item.kommentar }}
                      </v-tooltip>
                    </div>
                  </td>

                  <td class="action-buttons-cell">
                    <div class="action-menu-wrapper">
                      <v-menu
                        location="bottom"
                        :close-on-content-click="true"
                        transition="scale-transition"
                        offset="5"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            icon
                            size="small"
                            v-bind="props"
                            variant="text"
                            class="action-menu-btn"
                            color="grey-lighten-1"
                          >
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>

                        <v-list density="compact" class="action-menu-list">
                          <v-list-item
                            @click="bearbeiteEintrag(item)"
                            prepend-icon="mdi-pencil"
                            title="Bearbeiten"
                            class="action-menu-item edit-action"
                            value="edit"
                          ></v-list-item>

                          <v-divider class="menu-divider"></v-divider>

                          <v-list-item
                            @click="confirmDelete(item.id)"
                            prepend-icon="mdi-delete"
                            title="Löschen"
                            class="action-menu-item delete-action"
                            value="delete"
                          ></v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Keine Suchergebnisse -->
            <div
              v-if="filteredNebentaetigkeiten.length === 0"
              class="no-results"
            >
              <v-icon size="large" color="grey" class="mb-2"
                >mdi-file-search-outline</v-icon
              >
              <p>Keine passenden Einträge gefunden</p>
            </div>
          </div>

          <!-- Initiale Ansicht (ohne Suchbegriff) -->
          <div v-else class="search-placeholder">
            <v-icon size="x-large" color="grey" class="mb-4"
              >mdi-magnify</v-icon
            >
            <p>Gib einen Suchbegriff ein, um Nebentätigkeiten zu finden</p>
            <p class="text-caption mt-2">
              Du kannst nach Datum, Uhrzeit oder Kommentar suchen
            </p>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="nebentaetigkeitenSearchDialog = false"
          >
            <v-icon start>mdi-close</v-icon>
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vorlagen-Verwaltungs-Dialog -->
    <v-dialog v-model="vorlagenDialog" max-width="800px" scrollable>
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="success" size="large" class="mr-2">mdi-cog</v-icon>
          <div class="title-text">
            <div class="title-main">Vorlagen verwalten</div>
            <div class="title-sub">Erstelle und bearbeite deine Nebentätigkeiten-Vorlagen</div>
          </div>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-4">
          <!-- Neue Vorlage erstellen -->
          <div class="new-vorlage-section mb-6">
            <h3 class="section-title mb-3">
              <v-icon color="success" class="mr-2">mdi-plus-circle</v-icon>
              Neue Vorlage erstellen
            </h3>
            <v-row>
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="newVorlage.kommentar"
                  label="Kommentar / Bezeichnung"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  @keydown.enter="addVorlage"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-text</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="newVorlage.standard_dauer"
                  label="Standard-Dauer"
                  type="number"
                  variant="outlined"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field"
                  color="success"
                  min="1"
                  @keydown.enter="addVorlage"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="success" size="small">mdi-timer</v-icon>
                  </template>
                  <template v-slot:append-inner>
                    <span class="text-caption">min</span>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-btn
                  color="success"
                  variant="elevated"
                  block
                  :loading="loadingVorlagen"
                  :disabled="!newVorlage.kommentar.trim()"
                  @click="addVorlage"
                >
                  <v-icon start>mdi-plus</v-icon>
                  Hinzufügen
                </v-btn>
              </v-col>
            </v-row>
          </div>

          <v-divider class="my-4"></v-divider>

          <!-- Liste der Vorlagen -->
          <div class="vorlagen-list">
            <h3 class="section-title mb-3">
              <v-icon color="amber" class="mr-2">mdi-format-list-bulleted</v-icon>
              Meine Vorlagen ({{ benutzerVorlagen.length }})
            </h3>

            <div v-if="loadingVorlagen" class="text-center py-4">
              <v-progress-circular indeterminate color="success"></v-progress-circular>
            </div>

            <div v-else-if="benutzerVorlagen.length === 0" class="no-vorlagen">
              <v-icon size="large" color="grey">mdi-playlist-remove</v-icon>
              <p>Noch keine Vorlagen erstellt</p>
            </div>

            <v-list v-else bg-color="transparent">
              <v-list-item
                v-for="vorlage in benutzerVorlagen"
                :key="vorlage.id"
                class="vorlage-item mb-2"
              >
                <template v-slot:prepend>
                  <v-icon color="amber">mdi-bookmark</v-icon>
                </template>

                <v-list-item-title class="vorlage-title">
                  {{ vorlage.kommentar }}
                </v-list-item-title>

                <v-list-item-subtitle v-if="vorlage.standard_dauer" class="vorlage-subtitle">
                  Standard: {{ vorlage.standard_dauer }} Minuten
                </v-list-item-subtitle>
                <v-list-item-subtitle v-else class="vorlage-subtitle">
                  Keine Standard-Dauer
                </v-list-item-subtitle>

                <template v-slot:append>
                  <div class="vorlage-actions">
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      color="blue"
                      @click="openEditVorlage(vorlage)"
                    ></v-btn>
                    <v-btn
                      icon="mdi-delete"
                      size="small"
                      variant="text"
                      color="red"
                      @click="openDeleteVorlage(vorlage.id)"
                    ></v-btn>
                  </div>
                </template>
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="vorlagenDialog = false"
          >
            <v-icon start>mdi-close</v-icon>
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vorlage bearbeiten Dialog -->
    <v-dialog v-model="vorlagenEditDialog" max-width="600px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="blue" size="large" class="mr-2">mdi-pencil</v-icon>
          <div class="title-text">
            <div class="title-main">Vorlage bearbeiten</div>
          </div>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text v-if="editVorlage" class="dialog-content pa-4">
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="editVorlage.kommentar"
                label="Kommentar / Bezeichnung"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="blue"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="blue" size="small">mdi-text</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="editVorlage.standard_dauer"
                label="Standard-Dauer (optional)"
                type="number"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field"
                color="blue"
                min="1"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="blue" size="small">mdi-timer</v-icon>
                </template>
                <template v-slot:append-inner>
                  <span class="text-caption">min</span>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="vorlagenEditDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="blue"
            variant="elevated"
            :loading="loadingVorlagen"
            @click="updateVorlage"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Vorlage löschen Dialog -->
    <v-dialog v-model="vorlagenDeleteDialog" max-width="500px">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="red" size="large" class="mr-2">mdi-alert</v-icon>
          <div class="title-text">
            <div class="title-main">Vorlage löschen</div>
          </div>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-4">
          <p>Möchten Sie diese Vorlage wirklich löschen?</p>
          <p class="text-caption text-medium-emphasis mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            variant="text"
            @click="vorlagenDeleteDialog = false"
          >
            Abbrechen
          </v-btn>
          <v-btn
            color="red"
            variant="elevated"
            :loading="loadingVorlagen"
            @click="deleteVorlage"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();

// ======== Zustandsverwaltung ========
// Eingabemodus
const eingabeModus = ref("einfach"); // Standardmäßig einfache Eingabemaske

// Form-Zustände für detaillierte Eingabe
const startzeit = ref(getCurrentDateTime());
const endzeit = ref(getCurrentDateTime());
const kommentar = ref("");

// Form-Zustände für einfache Eingabe
const einfachDatum = ref(getCurrentDate());
const einfachMinuten = ref("60"); // Standardwert 60 Minuten
const einfachDurationInput = ref("");
const einfachUseHours = ref(false); // kept for backward compatibility in templates
const einfachStunden = ref("1");
const einfachMinutenPart = ref("0");
const formSubmitting = ref(false);

// Berechnung, ob das Formular gültig ist (abhängig vom Eingabemodus)
const isFormValid = computed(() => {
    if (eingabeModus.value === "einfach") {
      const total = computedEinfachTotalMinutes.value;
      return einfachDatum.value && total > 0 && kommentar.value;
  } else {
    return startzeit.value && endzeit.value && kommentar.value;
  }
});

// Listen-Zustände
const nebentaetigkeiten = ref([]);
const vordefinierteKommentare = ref([
  "Sepas Ausfall",
  "Reinigung",
  "Vorarbeitertätigkeiten",
  "Schulung",
  "GS Bearbeitung",
  "Inventur",
  "EA-87 H",
]);

// Vorlagen-Verwaltung
const benutzerVorlagen = ref([]);
const vorlagenDialog = ref(false);
const vorlagenEditDialog = ref(false);
const vorlagenDeleteDialog = ref(false);
const newVorlage = ref({
  kommentar: "",
  standard_dauer: null,
});
const editVorlage = ref(null);
const deleteVorlageId = ref(null);
const loadingVorlagen = ref(false);

// Kombinierte Kommentare: Systemvorlagen + Benutzervorlagen
const alleKommentare = computed(() => {
  const systemVorlagen = vordefinierteKommentare.value;
  const benutzerKommentare = benutzerVorlagen.value.map(v => v.kommentar);
  return [...systemVorlagen, ...benutzerKommentare].sort();
});

const loading = ref({
  initial: true,
  fetching: false,
  submitting: false,
  updating: false,
  deleting: false,
});

// UI-Zustände
const actionsVisible = ref(true);

// Dialog-Zustände
const editDialog = ref(false);
const deleteDialog = ref(false);
const nebentaetigkeitenSearchDialog = ref(false);

// Such- und Bearbeiten-Zustände
const nebentaetigkeitenSearchQuery = ref("");
const deleteId = ref(null);
const editItem = ref({});

// ======== Computed Properties ========
// Tabellenheader basierend auf sichtbaren Spalten
const headers = ref([
  { title: "Datum", key: "datum", align: "start", sortable: true },
  { title: "Startzeit", key: "startzeit", align: "center", sortable: true },
  { title: "Endzeit", key: "endzeit", align: "center", sortable: true },
  { title: "Dauer", key: "dauer", align: "center", sortable: true },
  { title: "Kommentar", key: "kommentar", align: "start", sortable: false },
  { title: "Aktionen", key: "actions", align: "center", sortable: false },
]);

// Dynamische Header basierend auf aktionsvisibilität
const computedHeaders = computed(() => {
  return actionsVisible.value
    ? headers.value
    : headers.value.filter((header) => header.key !== "actions");
});

// Gefilterte Nebentätigkeiten für die Suchfunktion
const filteredNebentaetigkeiten = computed(() => {
  if (!nebentaetigkeitenSearchQuery.value) return [];

  const query = nebentaetigkeitenSearchQuery.value.toLowerCase().trim();

  return nebentaetigkeiten.value.filter((item) => {
    // Suche im Kommentar
    if (item.kommentar && item.kommentar.toLowerCase().includes(query)) {
      return true;
    }

    // Suche im Datum
    if (formatDate(item.startzeit).toLowerCase().includes(query)) {
      return true;
    }

    // Suche in Startzeit
    if (formatTime(item.startzeit).toLowerCase().includes(query)) {
      return true;
    }

    // Suche in Endzeit
    if (formatTime(item.endzeit).toLowerCase().includes(query)) {
      return true;
    }

    // Suche in Dauer
    if (
      calculateDuration(item.startzeit, item.endzeit)
        .toLowerCase()
        .includes(query)
    ) {
      return true;
    }

    return false;
  });
});

// ======== UI Funktionen ========
// Toggle-Funktionen für UI-Elemente
const toggleActionsVisibility = () => {
  actionsVisible.value = !actionsVisible.value;
};

// Text-Hilfsfunktionen
const truncateText = (text, length) => {
  if (!text) return "—";
  if (text.length <= length) return text;
  return text.substring(0, length) + "...";
};

// Umschaltfunktionen für Datenansicht
const toggleDuration = (item) => {
  item.showMinutes = !item.showMinutes;
};

const toggleStartzeit = (item) => {
  item.showStartDate = !item.showStartDate;
};

// ======== Datum-Funktionen ========
// Aktuelle Datums- und Zeitwerte
function getCurrentDate() {
  const now = new Date();
  return now.toISOString().slice(0, 10); // YYYY-MM-DD
}

function getCurrentDateTime() {
  const now = new Date();
  const currentYear = now.getFullYear();

  // DST-Start: Letzter Sonntag im März
  const march31 = new Date(currentYear, 2, 31);
  const dstStart = new Date(march31);
  dstStart.setDate(march31.getDate() - march31.getDay());

  // DST-Ende: Letzter Sonntag im Oktober
  const october31 = new Date(currentYear, 9, 31);
  const dstEnd = new Date(october31);
  dstEnd.setDate(october31.getDate() - october31.getDay());

  // Wenn jetzt zwischen DST-Start (inkl.) und DST-Ende (exklusiv) liegt, gilt Sommerzeit (UTC+2)
  const offsetGermany = now >= dstStart && now < dstEnd ? 2 : 1;

  const germanyTime = new Date(now.getTime() + 3600000 * offsetGermany);
  return germanyTime.toISOString().slice(0, 16);
}

// Formatierungsfunktionen für Datum und Uhrzeit
const formatDate = (datetime) => {
  if (!datetime) return "—";
  try {
    const date = new Date(datetime);
    if (isNaN(date.getTime())) return "Ungültiges Datum";

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  } catch (e) {
    console.error("Datum-Formatierungsfehler:", e);
    return "Fehler beim Formatieren";
  }
};

const formatTime = (datetime) => {
  if (!datetime) return "—";
  try {
    const date = new Date(datetime);
    if (isNaN(date.getTime())) return "Ungültig";

    return date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  } catch (e) {
    console.error("Zeit-Formatierungsfehler:", e);
    return "Fehler";
  }
};

// Dauerfunktionen
const calculateDuration = (startzeit, endzeit) => {
  if (!startzeit || !endzeit) return "—";
  try {
    const start = new Date(startzeit);
    const end = new Date(endzeit);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "Ungültig";

    if (end < start) return "Negativ";

    const diff = end - start;
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);

    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")} h`;
  } catch (e) {
    console.error("Dauer-Berechnungsfehler:", e);
    return "Fehler";
  }
};

const calculateDurationInMinutes = (startzeit, endzeit) => {
  if (!startzeit || !endzeit) return 0;
  try {
    const start = new Date(startzeit);
    const end = new Date(endzeit);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    if (end < start) return 0;

    const diff = end - start;
    return Math.floor(diff / 60000);
  } catch (e) {
    console.error("Minuten-Berechnungsfehler:", e);
    return 0;
  }
};

// Adaptive parsing for einfache Eingabe: accepts "90" or "1:30"
const parseDurationString = (str) => {
  if (!str && str !== 0) return 0;
  const s = String(str).trim();
  if (!s) return 0;
  // If contains colon, parse HH:MM
  if (s.includes(":")) {
    const parts = s.split(":").map(p => p.trim());
    if (parts.length === 2) {
      const h = parseInt(parts[0], 10) || 0;
      const m = parseInt(parts[1], 10) || 0;
      return h * 60 + m;
    }
    return 0;
  }

  // Otherwise treat as total minutes
  const minutes = parseInt(s.replace(/[^0-9]/g, ""), 10);
  return isNaN(minutes) ? 0 : minutes;
};

const computedEinfachTotalMinutes = computed(() => {
  // priority: explicit einfachMinuten if non-empty and no duration input
  if (einfachDurationInput.value) {
    return parseDurationString(einfachDurationInput.value);
  }

  if (einfachUseHours.value) {
    const h = parseInt(einfachStunden.value || 0);
    const m = parseInt(einfachMinutenPart.value || 0);
    return h * 60 + m;
  }

  return parseInt(einfachMinuten.value || 0);
});

const parseEinfachDurationInput = () => {
  // normalize input into internals: keep einfachDurationInput and also set legacy fields
  const total = computedEinfachTotalMinutes.value;
  if (total >= 60) {
    einfachUseHours.value = true;
    einfachStunden.value = Math.floor(total / 60).toString();
    einfachMinutenPart.value = (total % 60).toString();
    einfachMinuten.value = total.toString();
  } else {
    einfachUseHours.value = false;
    einfachMinuten.value = total.toString();
    einfachStunden.value = "0";
    einfachMinutenPart.value = "0";
  }
};

// ======== API-Interaktionen ========
// Nebentätigkeiten abrufen
const ladeNebentaetigkeiten = async () => {
  if (!authStore.user?.id) return;

  loading.value.fetching = true;

  try {
    const response = await axios.get(`${API_BASE_URL}nebentaetigkeiten.php`, {
      params: { action: "get" },
      withCredentials: true,
    });

    // Erwartet wird "records" als Datenfeld von der API
    if (response.data && response.data.records) {
      nebentaetigkeiten.value = response.data.records.map((item) => ({
        ...item,
        showMinutes: false,
        showStartDate: false,
        showEndDate: false,
      }));

      // Nach Datum sortieren (neueste zuerst)
      nebentaetigkeiten.value.sort(
        (a, b) => new Date(b.startzeit) - new Date(a.startzeit)
      );
    } else {
      console.warn("Unerwartetes Antwortformat:", response.data);
      toast.warning("Die Daten konnten nicht korrekt geladen werden.");
      nebentaetigkeiten.value = [];
    }
  } catch (error) {
    console.error("Fehler beim Laden der Nebentätigkeiten:", error);
    toast.error("Fehler beim Laden der Nebentätigkeiten");
    nebentaetigkeiten.value = [];
  } finally {
    loading.value.fetching = false;
    loading.value.initial = false;
  }
};

// Nebentätigkeit speichern - Angepasst für beide Eingabemodi
const speichereNebentaetigkeit = async () => {
  if (!authStore.user?.id) {
    toast.error("Bitte melde dich an, um Nebentätigkeiten zu erfassen.");
    return;
  }

  // Validierung je nach Modus
  if (eingabeModus.value === "einfach") {
    if (!einfachDatum.value || !einfachMinuten.value || !kommentar.value) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }

    if (parseInt(einfachMinuten.value) <= 0) {
      toast.error("Die Dauer muss größer als 0 sein!");
      return;
    }
  } else {
    if (!startzeit.value || !endzeit.value || !kommentar.value) {
      toast.error("Bitte fülle alle Felder aus.");
      return;
    }

    if (new Date(endzeit.value) < new Date(startzeit.value)) {
      toast.error("Die Endzeit darf nicht vor der Startzeit liegen!");
      return;
    }
  }

  function formatLocalDate(date) {
    const pad = (num) => num.toString().padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  formSubmitting.value = true;

  try {
    // Daten je nach Eingabemodus vorbereiten
    let finalStartzeit, finalEndzeit;

    if (eingabeModus.value === "einfach") {
      // Bei einfacher Eingabe: Datum + 0 Minuten = Startzeit
      // Datum + eingegebene Minuten = Endzeit

      const startDate = new Date(einfachDatum.value);
      startDate.setHours(7, 0, 0, 0); // Setze auf 00:00 Uhr
      finalStartzeit = formatLocalDate(startDate);
      const minutesToAdd = computedEinfachTotalMinutes.value || 0;
      const endDate = new Date(startDate.getTime() + minutesToAdd * 60000);
      finalEndzeit = formatLocalDate(endDate);
    } else {
      // Bei detaillierter Eingabe: direkte Übernahme
      finalStartzeit = startzeit.value;
      finalEndzeit = endzeit.value;
    }

    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten.php`,
      {
        action: "add",
        startzeit: finalStartzeit,
        endzeit: finalEndzeit,
        kommentar: kommentar.value,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      await ladeNebentaetigkeiten();
      toast.success("Nebentätigkeit erfolgreich gespeichert!");
      resetForm();
    } else {
      toast.error(response.data?.message || "Fehler beim Speichern!");
    }
  } catch (error) {
    console.error("Fehler beim Speichern der Nebentätigkeit:", error);
    toast.error("Verbindungsfehler beim Speichern.");
  } finally {
    formSubmitting.value = false;
  }
};

// Formular zurücksetzen - Angepasst für beide Modi
const resetForm = () => {
  startzeit.value = getCurrentDateTime();
  endzeit.value = getCurrentDateTime();
  einfachDatum.value = getCurrentDate();
  einfachMinuten.value = "60";
  einfachDurationInput.value = "";
  einfachUseHours.value = false;
  einfachStunden.value = "1";
  einfachMinutenPart.value = "0";
  kommentar.value = "";
};

// Nur Zahlen im Minuten-Feld erlauben
const allowOnlyNumbers = (event) => {
  const keyCode = event.keyCode || event.which;
  const keyValue = String.fromCharCode(keyCode);

  if (!/^\d+$/.test(keyValue)) {
    event.preventDefault();
  }
};

// ======== Bearbeiten-Funktionen ========
// Bearbeiten-Dialog öffnen
const bearbeiteEintrag = (item) => {
  editItem.value = { ...item };
  editDialog.value = true;

  // Fokus setzen für Keyboard-Navigation
  nextTick(() => {
    const dialogElement = document.querySelector(".edit-dialog");
    if (dialogElement) dialogElement.focus();
  });
};

// Nebentätigkeit aktualisieren
const updateNebentaetigkeit = async () => {
  if (
    !editItem.value.startzeit ||
    !editItem.value.endzeit ||
    !editItem.value.kommentar
  ) {
    toast.error("Bitte alle Felder ausfüllen!");
    return;
  }

  if (new Date(editItem.value.endzeit) < new Date(editItem.value.startzeit)) {
    toast.error("Die Endzeit darf nicht vor der Startzeit liegen!");
    return;
  }

  loading.value.updating = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten.php`,
      {
        action: "update",
        id: editItem.value.id,
        startzeit: editItem.value.startzeit,
        endzeit: editItem.value.endzeit,
        kommentar: editItem.value.kommentar,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      await ladeNebentaetigkeiten();
      toast.success("Nebentätigkeit erfolgreich aktualisiert!");
      editDialog.value = false;
    } else {
      toast.error(response.data?.message || "Fehler beim Aktualisieren!");
    }
  } catch (error) {
    console.error("Fehler beim Aktualisieren:", error);
    toast.error("Verbindungsfehler beim Aktualisieren.");
  } finally {
    loading.value.updating = false;
  }
};

// ======== Löschen-Funktionen ========
// Löschen-Dialog öffnen
const confirmDelete = (id) => {
  deleteId.value = id;
  deleteDialog.value = true;
};

// Nebentätigkeit löschen
const deleteNebentaetigkeit = async () => {
  loading.value.deleting = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten.php`,
      {
        action: "delete",
        id: deleteId.value,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      await ladeNebentaetigkeiten();
      toast.success("Nebentätigkeit erfolgreich gelöscht!");
      deleteDialog.value = false;
    } else {
      toast.error(response.data?.message || "Fehler beim Löschen!");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Verbindungsfehler beim Löschen.");
  } finally {
    loading.value.deleting = false;
  }
};

// ======== Vorlagen-Verwaltung ========
// Vorlagen laden
const ladeVorlagen = async () => {
  loadingVorlagen.value = true;
  try {
    const response = await axios.get(
      `${API_BASE_URL}nebentaetigkeiten_vorlagen.php?action=get`,
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      benutzerVorlagen.value = response.data.vorlagen;
    }
  } catch (error) {
    console.error("Fehler beim Laden der Vorlagen:", error);
    toast.error("Fehler beim Laden der Vorlagen");
  } finally {
    loadingVorlagen.value = false;
  }
};

// Vorlage hinzufügen
const addVorlage = async () => {
  if (!newVorlage.value.kommentar.trim()) {
    toast.error("Bitte einen Kommentar eingeben!");
    return;
  }

  loadingVorlagen.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten_vorlagen.php`,
      {
        action: "add",
        kommentar: newVorlage.value.kommentar,
        standard_dauer: newVorlage.value.standard_dauer || null,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      toast.success("Vorlage erfolgreich erstellt!");
      await ladeVorlagen();
      newVorlage.value = { kommentar: "", standard_dauer: null };
    } else {
      toast.error(response.data?.message || "Fehler beim Erstellen der Vorlage!");
    }
  } catch (error) {
    console.error("Fehler beim Erstellen der Vorlage:", error);
    toast.error("Verbindungsfehler beim Erstellen der Vorlage");
  } finally {
    loadingVorlagen.value = false;
  }
};

// Vorlage bearbeiten öffnen
const openEditVorlage = (vorlage) => {
  editVorlage.value = { ...vorlage };
  vorlagenEditDialog.value = true;
};

// Vorlage aktualisieren
const updateVorlage = async () => {
  if (!editVorlage.value.kommentar.trim()) {
    toast.error("Bitte einen Kommentar eingeben!");
    return;
  }

  loadingVorlagen.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten_vorlagen.php`,
      {
        action: "update",
        id: editVorlage.value.id,
        kommentar: editVorlage.value.kommentar,
        standard_dauer: editVorlage.value.standard_dauer || null,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      toast.success("Vorlage erfolgreich aktualisiert!");
      await ladeVorlagen();
      vorlagenEditDialog.value = false;
    } else {
      toast.error(response.data?.message || "Fehler beim Aktualisieren!");
    }
  } catch (error) {
    console.error("Fehler beim Aktualisieren:", error);
    toast.error("Verbindungsfehler beim Aktualisieren");
  } finally {
    loadingVorlagen.value = false;
  }
};

// Vorlage löschen Dialog öffnen
const openDeleteVorlage = (id) => {
  deleteVorlageId.value = id;
  vorlagenDeleteDialog.value = true;
};

// Vorlage löschen
const deleteVorlage = async () => {
  loadingVorlagen.value = true;
  try {
    const response = await axios.post(
      `${API_BASE_URL}nebentaetigkeiten_vorlagen.php`,
      {
        action: "delete",
        id: deleteVorlageId.value,
      },
      { withCredentials: true }
    );

    if (response.data && response.data.status === "success") {
      toast.success("Vorlage erfolgreich gelöscht!");
      await ladeVorlagen();
      vorlagenDeleteDialog.value = false;
    } else {
      toast.error(response.data?.message || "Fehler beim Löschen!");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Verbindungsfehler beim Löschen");
  } finally {
    loadingVorlagen.value = false;
  }
};

// ======== Beobachter für Authentifizierungsänderungen ========
watch(
  () => authStore.user,
  (newUser, oldUser) => {
    if (newUser && !oldUser) {
      ladeNebentaetigkeiten();
      ladeVorlagen();
    } else if (!newUser && oldUser) {
      nebentaetigkeiten.value = [];
      benutzerVorlagen.value = [];
    }
  }
);

// Auto-Fill für vordefinierte Nebentätigkeiten
watch(kommentar, (newValue) => {
  if (!newValue) return;

  // System-Vorlagen
  if (newValue === "EA-87 H") {
    einfachMinuten.value = "20";
    einfachDurationInput.value = "20";
    return;
  }

  // Benutzer-Vorlagen
  const vorlage = benutzerVorlagen.value.find((v) => v.kommentar === newValue);
    if (vorlage && vorlage.standard_dauer) {
    const total = parseInt(vorlage.standard_dauer, 10) || 0;
    // set adaptive input string
    if (total >= 60) {
      const h = Math.floor(total / 60);
      const m = total % 60;
      einfachDurationInput.value = `${h}:${String(m).padStart(2, '0')}`;
    } else {
      einfachDurationInput.value = `${total}`;
    }
    // keep legacy fields in sync
    if (total >= 60) {
      einfachUseHours.value = true;
      einfachStunden.value = Math.floor(total / 60).toString();
      einfachMinutenPart.value = (total % 60).toString();
      einfachMinuten.value = total.toString();
    } else {
      einfachUseHours.value = false;
      einfachMinuten.value = total.toString();
      einfachStunden.value = "0";
      einfachMinutenPart.value = "0";
    }
  }
});

// ======== Lebenszyklus-Hooks ========
// Daten beim Laden der Komponente abrufen
onMounted(() => {
  if (authStore.user) {
    ladeNebentaetigkeiten();
    ladeVorlagen();
  }

  // Tastaturkürzel für Escape-Taste hinzufügen
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (editDialog.value) editDialog.value = false;
      else if (deleteDialog.value) deleteDialog.value = false;
      else if (nebentaetigkeitenSearchDialog.value)
        nebentaetigkeitenSearchDialog.value = false;
      else if (vorlagenDialog.value) vorlagenDialog.value = false;
      else if (vorlagenEditDialog.value) vorlagenEditDialog.value = false;
      else if (vorlagenDeleteDialog.value) vorlagenDeleteDialog.value = false;
    }
  });
});
</script>

<style scoped>
/* ======== Container & Basis-Layout ======== */
.nebentaetigkeiten-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

/* ======== Karten & Container ======== */
.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.card-divider {
  opacity: 0.1;
}

.dialog-card {
  background-color: rgba(30, 30, 35, 0.9);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.dialog-title {
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 500;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dialog-divider {
  opacity: 0.1;
}

.dialog-content {
  padding: 20px;
}

.dialog-actions {
  padding: 12px 16px;
}

.warning-header {
  background: rgba(var(--v-theme-error), 0.1);
}

/* ======== Eingabefelder & Formulare ======== */
.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.submit-btn {
  height: 50px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-btn[disabled] {
  opacity: 0.65 !important;
  box-shadow: none !important;
  background-color: rgba(60, 60, 60, 0.8) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.submit-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.6s;
}

.submit-btn:hover:not([disabled]) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.submit-btn:hover:not([disabled])::before {
  left: 100%;
}

.search-field :deep(.v-field__prepend-inner) {
  opacity: 0.8;
}

/* ======== Tabellen-Design ======== */
.data-table {
  background: transparent !important;
}

.data-table :deep(table) {
  background: transparent !important;
  border-spacing: 0;
}

.data-table :deep(tr:hover td) {
  background: rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(th) {
  background: rgba(20, 20, 25, 0.5) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(td) {
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.85);
}

/* Paginierung anpassen */
.data-table :deep(.v-data-table-footer) {
  background: transparent !important;
}

.data-table :deep(.v-data-table-footer__items-per-page) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-select .v-field) {
  background: rgba(30, 30, 40, 0.5) !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

.data-table :deep(.v-data-table-footer .v-btn) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-btn--disabled) {
  color: rgba(255, 255, 255, 0.3) !important;
}

.date-cell {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  background-color: rgba(76, 175, 80, 0.1);
  display: inline-block;
}

.date-cell:hover {
  background-color: rgba(76, 175, 80, 0.2);
  transform: translateY(-2px);
}

.time-chip {
  font-family: "Roboto Mono", monospace;
  font-size: 0.85rem;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: rgba(76, 175, 80, 0.1);
  display: inline-block;
  color: rgba(76, 175, 80, 0.9);
}

.duration-chip {
  font-family: "Roboto Mono", monospace;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: rgba(76, 175, 80, 0.15);
  border-left: 2px solid rgba(76, 175, 80, 0.7);
  display: inline-block;
  cursor: pointer;
  transition: all 0.2s ease;
}

.duration-chip:hover {
  background-color: rgba(76, 175, 80, 0.25);
  transform: translateY(-2px);
}

.kommentar-cell {
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

/* ======== Aktionsbuttons in Tabellen ======== */
.action-buttons-cell {
  width: 48px !important;
  text-align: center;
}

.action-menu-wrapper {
  display: flex;
  justify-content: center;
}

.action-menu-btn {
  opacity: 0.7;
  transition: all 0.2s ease;
}

.action-menu-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.action-menu-list {
  background-color: rgba(35, 35, 40, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
  min-width: 180px;
}

.action-menu-item {
  transition: background-color 0.2s ease;
}

.edit-action:hover {
  background-color: rgba(76, 175, 80, 0.1) !important;
}

.edit-action:hover :deep(.v-list-item-title),
.edit-action:hover :deep(.v-icon) {
  color: rgba(76, 175, 80, 0.9) !important;
}

.delete-action:hover {
  background-color: rgba(244, 67, 54, 0.1) !important;
}

.delete-action:hover :deep(.v-list-item-title),
.delete-action:hover :deep(.v-icon) {
  color: rgba(244, 67, 54, 0.9) !important;
}

.menu-divider {
  opacity: 0.1;
}

.action-buttons {
  display: flex;
  justify-content: center;
}

.action-btn {
  opacity: 0.8;
  transition: all 0.2s ease;
}

.action-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
}

/* ======== Leerstand & Platzhaltermeldungen ======== */
.no-data-message,
.no-results,
.search-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.search-placeholder {
  padding: 60px 20px;
}

.search-results {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 960px) {
  .action-buttons-cell {
    width: 48px;
  }
}

@media (max-width: 600px) {
  .dialog-content {
    padding: 16px;
  }

  .dialog-title {
    font-size: 1rem;
    padding: 12px 16px;
  }

  .data-table :deep(th),
  .data-table :deep(td) {
    padding: 8px 12px !important;
  }

  .search-placeholder {
    padding: 40px 16px;
  }
}

/* removed toggle button styles (single adaptive input now) */

/* ======== Vorlagen-Verwaltung ======== */
.new-vorlage-section {
  background: rgba(35, 35, 45, 0.5);
  border-radius: 12px;
  padding: 20px;
  border-left: 4px solid rgba(76, 175, 80, 0.8);
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.vorlage-item {
  background: rgba(35, 35, 45, 0.6);
  border-radius: 10px;
  border-left: 3px solid rgba(255, 193, 7, 0.8);
  transition: all 0.2s ease;
}

.vorlage-item:hover {
  background: rgba(40, 40, 50, 0.7);
  transform: translateX(3px);
}

.vorlage-title {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.vorlage-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
}

.vorlage-actions {
  display: flex;
  gap: 4px;
}

.no-vorlagen {
  text-align: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.5);
}

.no-vorlagen p {
  margin-top: 12px;
  font-size: 0.95rem;
}

.gap-2 {
  gap: 8px;
}
</style>
